import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { publicAPI } from '../services/api';

const ENGAGEMENT_TYPES = [
  { key: 'client',     label: 'Hire Us',         icon: '◉', desc: 'Get a service or project quote' },
  { key: 'partner',    label: 'Partner',          icon: '⬡', desc: 'Institutional or business partnership' },
  { key: 'volunteer',  label: 'Volunteer',        icon: '◐', desc: 'Give your time and skills' },
  { key: 'intern',     label: 'Intern',           icon: '◈', desc: 'Join the team as an intern' },
  { key: 'career',     label: 'Career',           icon: '◎', desc: 'Apply for a full-time role' },
  { key: 'message',    label: 'Message',          icon: '◻', desc: 'Just say hello or ask anything' },
];

function ContactForm({ type, serviceSlug }) {
  const isApp  = !['message'].includes(type);
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', organization: '',
    subject: '', message: '', motivation: '', service: serviceSlug || '',
  });
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.full_name || !form.email) return;
    setStatus('loading');
    try {
      if (type === 'message') {
        await publicAPI.contact({
          full_name: form.full_name,
          email:     form.email,
          phone:     form.phone,
          subject:   form.subject,
          message:   form.message,
        });
      } else {
        await publicAPI.apply({
          type,
          full_name:    form.full_name,
          email:        form.email,
          phone:        form.phone,
          organization: form.organization,
          motivation:   form.motivation || form.message,
          service_interest: form.service || undefined,
        });
      }
      setStatus('success');
    } catch (err) {
      setErrMsg(err.response?.data?.error || 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  if (status === 'success') return (
    <div style={{
      padding: '48px', textAlign: 'center',
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 'var(--r-xl)',
      borderTop: '3px solid var(--gold)',
    }}>
      <div style={{ fontSize: '3rem', marginBottom: 16, color: 'var(--gold)' }}>✓</div>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', marginBottom: 12 }}>
        Message Received!
      </h3>
      <p style={{ color: 'var(--t-secondary)', lineHeight: 1.7, maxWidth: 400, margin: '0 auto 28px' }}>
        Thank you, <strong style={{ color: 'var(--t-primary)' }}>{form.full_name}</strong>.
        We'll be in touch at <strong style={{ color: 'var(--t-primary)' }}>{form.email}</strong> very soon.
      </p>
      <button onClick={() => setStatus('idle')} className="btn btn-outline" style={{ margin: '0 auto' }}>
        Send Another →
      </button>
    </div>
  );

  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 'var(--r-xl)', padding: '36px',
      borderTop: '3px solid var(--gold)',
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        <div className="field">
          <label>Full Name *</label>
          <input value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Your full name" />
        </div>
        <div className="field">
          <label>Email Address *</label>
          <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@example.com" />
        </div>
        <div className="field">
          <label>Phone</label>
          <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+250 7xx xxx xxx" />
        </div>
        {isApp && (
          <div className="field">
            <label>Organization / Company</label>
            <input value={form.organization} onChange={e => set('organization', e.target.value)} placeholder="Optional" />
          </div>
        )}
        {type === 'message' && (
          <div className="field" style={{ gridColumn: '1/-1' }}>
            <label>Subject</label>
            <input value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="What's this about?" />
          </div>
        )}
        {type === 'client' && (
          <div className="field" style={{ gridColumn: '1/-1' }}>
            <label>Service of Interest</label>
            <input value={form.service} onChange={e => set('service', e.target.value)} placeholder="e.g. Web Development, UI/UX Design…" />
          </div>
        )}
        <div className="field" style={{ gridColumn: '1/-1' }}>
          <label>{type === 'message' ? 'Message *' : 'Tell Us About Yourself *'}</label>
          <textarea
            value={type === 'message' ? form.message : form.motivation}
            onChange={e => set(type === 'message' ? 'message' : 'motivation', e.target.value)}
            rows={5}
            placeholder={
              type === 'message' ? 'What would you like to tell us?' :
              type === 'client'  ? 'Describe your project or what you\'d like us to build...' :
              type === 'partner' ? 'Tell us about your organization and how you\'d like to collaborate...' :
              type === 'volunteer' ? 'What skills do you bring and what motivates you to volunteer?' :
              type === 'intern'  ? 'Tell us about yourself, your skills, and what you hope to learn...' :
              'Tell us about your experience, skills, and career goals...'
            }
          />
        </div>
      </div>

      {status === 'error' && (
        <div style={{
          marginTop: 16, padding: '12px 16px',
          background: 'rgba(240,96,96,0.1)', border: '1px solid rgba(240,96,96,0.3)',
          borderRadius: 'var(--r-sm)', color: 'var(--red)', fontSize: '0.875rem',
        }}>{errMsg}</div>
      )}

      <button
        onClick={handleSubmit}
        disabled={!form.full_name || !form.email || status === 'loading'}
        className="btn btn-primary"
        style={{
          marginTop: 22, width: '100%', justifyContent: 'center',
          opacity: (!form.full_name || !form.email) ? 0.5 : 1,
          fontSize: '0.95rem',
        }}
      >
        {status === 'loading' ? 'Sending…' : 'Send Message →'}
      </button>
    </div>
  );
}

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const initType = searchParams.get('type') || 'message';
  const serviceSlug = searchParams.get('service') || '';
  const [activeType, setActiveType] = useState(initType);

  useEffect(() => {
    const t = searchParams.get('type');
    if (t) setActiveType(t);
  }, [searchParams]);

  const activeInfo = ENGAGEMENT_TYPES.find(t => t.key === activeType) || ENGAGEMENT_TYPES[0];

  return (
    <div>
      {/* Hero */}
      <section style={{
        paddingTop: 'calc(var(--nav-h) + clamp(60px,8vw,100px))',
        paddingBottom: 'clamp(60px,8vw,100px)',
        background: 'linear-gradient(160deg, var(--bg-1) 0%, var(--bg-0) 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', bottom: -120, left: -80, width: 400, height: 400,
          background: 'radial-gradient(circle, rgba(232,164,58,0.06) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="section-label label" style={{ marginBottom: 20 }}>Get in Touch</div>
          <h1 className="display-lg" style={{ marginBottom: 16 }}>
            Let's Build<br />
            <span style={{ color: 'var(--gold)' }}>Something Together</span>
          </h1>
          <p className="body-lg" style={{ color: 'var(--t-secondary)', maxWidth: 520 }}>
            Whether you want to hire us, partner, volunteer, or simply say hello — 
            we'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Type selector */}
      <section style={{ background: 'var(--bg-0)', padding: 'clamp(40px,5vw,60px) 0' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 40 }}>
            {ENGAGEMENT_TYPES.map(t => (
              <button key={t.key} onClick={() => setActiveType(t.key)} style={{
                padding: '18px 16px', borderRadius: 'var(--r-md)',
                background: activeType === t.key ? 'rgba(232,164,58,0.08)' : 'var(--bg-card)',
                border: `1px solid ${activeType === t.key ? 'var(--gold)' : 'var(--border)'}`,
                cursor: 'pointer', textAlign: 'left',
                transition: 'all 0.15s',
              }}>
                <div style={{
                  fontSize: '1.25rem', marginBottom: 8,
                  color: activeType === t.key ? 'var(--gold)' : 'var(--t-secondary)',
                }}>{t.icon}</div>
                <div style={{
                  fontFamily: 'var(--font-display)', fontSize: '0.9rem', fontWeight: 600,
                  color: activeType === t.key ? 'var(--gold)' : 'var(--t-primary)',
                  marginBottom: 4,
                }}>{t.label}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--t-muted)', lineHeight: 1.4 }}>{t.desc}</div>
              </button>
            ))}
          </div>

          {/* Form */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 40, alignItems: 'start' }}>
            <ContactForm type={activeType} serviceSlug={serviceSlug} key={activeType} />

            {/* Info sidebar */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{
                padding: '24px', background: 'var(--bg-card)',
                border: '1px solid var(--border)', borderRadius: 'var(--r-lg)',
              }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, marginBottom: 16, fontSize: '1rem' }}>
                  Contact Details
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {[
                    { icon: '◉', label: 'Email',    value: 'hello@millehills.com', color: 'var(--gold)' },
                    { icon: '◐', label: 'Location', value: 'Gisenyi, Rwanda', color: 'var(--teal)' },
                    { icon: '◈', label: 'Response', value: 'Within 48 hours', color: '#a78bfa' },
                  ].map(i => (
                    <div key={i.label} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <span style={{ color: i.color, fontSize: 16, flexShrink: 0 }}>{i.icon}</span>
                      <div>
                        <div className="label" style={{ color: 'var(--t-muted)', fontSize: '0.6rem' }}>{i.label}</div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--t-primary)', marginTop: 1 }}>{i.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{
                padding: '24px', background: 'var(--bg-card)',
                border: '1px solid var(--border)', borderRadius: 'var(--r-lg)',
              }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, marginBottom: 12, fontSize: '0.95rem' }}>
                  Other Ways to Connect
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <Link to="/programs" style={{ fontSize: '0.85rem', color: 'var(--t-secondary)', display: 'flex', gap: 8, alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--gold)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--t-secondary)'}
                  >◐ Browse our training programs</Link>
                  <Link to="/services" style={{ fontSize: '0.85rem', color: 'var(--t-secondary)', display: 'flex', gap: 8, alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--gold)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--t-secondary)'}
                  >◉ View the full services catalog</Link>
                  <Link to="/about" style={{ fontSize: '0.85rem', color: 'var(--t-secondary)', display: 'flex', gap: 8, alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--gold)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--t-secondary)'}
                  >⬡ Learn about the ecosystem</Link>
                </div>
              </div>

              <div style={{
                padding: '20px 24px',
                background: 'rgba(232,164,58,0.05)',
                border: '1px solid rgba(232,164,58,0.2)',
                borderRadius: 'var(--r-lg)',
                fontSize: '0.8rem', color: 'var(--t-muted)', lineHeight: 1.7,
              }}>
                <span style={{ color: 'var(--gold)', fontWeight: 600 }}>Gisenyi, Rwanda</span><br />
                Rubavu District · Western Province<br />
                <span style={{ opacity: 0.6 }}>Founder-Led · Constitution-Protected</span>
              </div>
            </div>
          </div>
          <style>{`@media(max-width:768px){.contact-layout{grid-template-columns:1fr!important}}`}</style>
        </div>
      </section>
    </div>
  );
}